/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import {
  defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS,
  exchangeRateStatus,
} from 'config';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { bulkUpdate } from 'helpers/updateMutationsHelper';
import { Query, Mutation } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import ExchangeRateList from './List';

/**
 * Query to fetch exchange rates from api
 */
export const EXCHANGE_RATES_QUERY = gql`
  query ExchangeRateList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $status: [FilterExchangeRateStatus]
  ) {
    exchangeRates(
      first: $first
      after: $after
      last: $last
      before: $before
      status: $status
    ) @connection(key: "exchangeRates") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          status
          createdOn
          createdBy {
            id
            fullname
          }
          updatedOn
          updatedBy {
            id
            fullname
          }
          currencyFrom
          currencyTo
          rate
          startDate
          endDate
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Mutation to enable exchange rates
 */
export const ENABLE_EXCHANGE_RATE_MUTATION = gql`
  mutation($ids: [ID]!) {
    activateExchangeRate(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to disable exchange rates
 */
export const DISABLE_EXCHANGE_RATE_MUTATION = gql`
  mutation($ids: [ID]!) {
    deactivateExchangeRate(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Default filters in ExchangeRateList
 */
export const defaultFilters = {};

/**
 * @class
 * @extends Component
 */
class ExchangeRateListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;
    this.state = {
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      filters: {
        ...initFilters,
      },
      pagination: { direction: direction || null, cursor: cursor || undefined },
      itemProcessing: [],
      failedIds: [],
      showNotification: false,
    };
    this.Compose = adopt({
      mutateExchangeRateEnable: this.exchangeRateEnable,
      mutateExchangeRateDisable: this.exchangeRateDisable,
      query: this.listExchangeRate,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listExchangeRate = ({ render }) => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={EXCHANGE_RATES_QUERY}
        variables={{
          ...filters,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Mutation enable an exchange rate callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  exchangeRateEnable = ({ render }) => {
    const { itemProcessing, filters } = this.state;
    return (
      <Mutation
        mutation={ENABLE_EXCHANGE_RATE_MUTATION}
        onCompleted={this.mutationCompleted(
          'activateExchangeRate',
          itemProcessing,
        )}
        update={(cache, { data: { activateExchangeRate } }) => {
          const data = {
            status: exchangeRateStatus.ENABLED,
            __typename: 'AdminExchangeRateType',
          };

          bulkUpdate(
            cache,
            'AdminExchangeRateType',
            'Enable',
            'status',
            data,
            activateExchangeRate.successIds,
          );

          if (
            filters.status &&
            filters.status.indexOf(exchangeRateStatus.ENABLED) === -1
          ) {
            const variables = { last: activateExchangeRate.successIds.length };
            const { exchangeRates } = cache.readQuery({
              query: EXCHANGE_RATES_QUERY,
            });

            const newEdges = exchangeRates.edges.filter(
              edge =>
                activateExchangeRate.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: EXCHANGE_RATES_QUERY,
              data: {
                exchangeRates: {
                  ...exchangeRates,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {handleExchangeRateEnable => render({ handleExchangeRateEnable })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation disable an exchange rate callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  exchangeRateDisable = ({ render }) => {
    const { itemProcessing, filters } = this.state;
    return (
      <Mutation
        mutation={DISABLE_EXCHANGE_RATE_MUTATION}
        onCompleted={this.mutationCompleted(
          'deactivateExchangeRate',
          itemProcessing,
        )}
        update={(cache, { data: { deactivateExchangeRate } }) => {
          const data = {
            status: exchangeRateStatus.DISABLED,
            __typename: 'AdminExchangeRateType',
          };

          bulkUpdate(
            cache,
            'AdminExchangeRateType',
            'Disable',
            'status',
            data,
            deactivateExchangeRate.successIds,
          );

          if (
            filters.status &&
            filters.status.indexOf(exchangeRateStatus.DISABLED) === -1
          ) {
            const variables = {
              last: deactivateExchangeRate.successIds.length,
            };
            const { exchangeRates } = cache.readQuery({
              query: EXCHANGE_RATES_QUERY,
            });

            const newEdges = exchangeRates.edges.filter(
              edge =>
                deactivateExchangeRate.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: EXCHANGE_RATES_QUERY,
              data: {
                exchangeRates: {
                  ...exchangeRates,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {handleExchangeRateDisable => render({ handleExchangeRateDisable })}
      </Mutation>
    );
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const { pageInfo, edges: newEdges } = options.fetchMoreResult.exchangeRates;
    if (newEdges.length !== 0) {
      return {
        exchangeRates: {
          __typename: previousResult.exchangeRates.__typename,
          edges: [...previousResult.exchangeRates.edges, ...newEdges],
          pageInfo: {
            ...previousResult.exchangeRates.pageInfo,
            hasNextPage: pageInfo.hasNextPage,
          },
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * OnComplete mutations callback.
   *
   * @param {Object} field - Target field of mutation.
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = (field, itemProcessing) => async data => {
    const failedIds = itemProcessing.filter(
      item => !data[field].successIds.includes(item),
    );
    this.setState({
      itemProcessing: [],
      failedIds,
      showNotification: failedIds.length > 0,
    });
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: {
        ...filters,
      },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      numberOfRowsSelected,
    });

    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ showNotification: false });
  };

  /**
   * @description
   * Render ExchangeRateList component.
   */
  render() {
    const {
      numberOfRowsSelected,
      filters,
      itemProcessing,
      failedIds,
      pagination,
      showNotification,
    } = this.state;
    return (
      <this.Compose>
        {({
          mutateExchangeRateEnable: { handleExchangeRateEnable },
          mutateExchangeRateDisable: { handleExchangeRateDisable },
          query: { loading, data },
        }) => (
          <ExchangeRateList
            data={data}
            exchangeRateEnable={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleExchangeRateEnable({ variables: { ids: items } });
            }}
            exchangeRateDisable={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleExchangeRateDisable({ variables: { ids: items } });
            }}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            refresh={this.handleRefresh}
            restartFilters={this.handleRestartFilters}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            showNotification={showNotification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * ExchangeRateListContainer PropTypes
 * @param initialFilters - prop initial Filters to apply
 */

ExchangeRateListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};

export default ExchangeRateListContainer;
